/**
 * submenu-keyboard.js
 *
 * Makes hover-only navigation submenus reachable by keyboard (SBMR-79).
 *
 * interactable.min.js binds hoverIntent on pointer devices and explicitly
 * unbinds the click handler there, so on desktop a submenu can only be opened
 * with a mouse. It toggles "is-action__hover" on the parent item, and the
 * theme's own CSS already makes that class reveal the panel with
 * pointer-events auto, so driving the same class is all that is needed.
 *
 * Parent items are real links to category pages, so Enter is left alone and
 * must keep navigating. Focus opens the panel, Arrow Down moves into it, and
 * Escape closes it and returns focus to the parent.
 */
(function () {
  'use strict';

  var ITEM = '.t4s-menu-item.has--children';
  var OPEN = 'is-action__hover';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var uid = 0;

  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var s = window.getComputedStyle(el);
    return s.display !== 'none' && s.visibility !== 'hidden' && s.visibility !== 'collapse';
  }

  function submenuOf(item) {
    for (var i = 0; i < item.children.length; i++) {
      if (item.children[i].classList.contains('t4s-sub-menu')) return item.children[i];
    }
    return null;
  }

  function triggerOf(item) {
    for (var i = 0; i < item.children.length; i++) {
      var c = item.children[i];
      if (c.tagName === 'A' || c.tagName === 'BUTTON') return c;
    }
    return null;
  }

  function wire(item) {
    if (item.dataset.kbWired === 'true') return;
    var sub = submenuOf(item);
    var trigger = triggerOf(item);
    if (!sub || !trigger) return;
    item.dataset.kbWired = 'true';

    if (!sub.id) sub.id = 't4s-submenu-' + (++uid);
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-controls', sub.id);

    // Single source of truth: mirror the class the theme already toggles, so
    // hover and keyboard both report the same state.
    function sync() {
      trigger.setAttribute('aria-expanded', item.classList.contains(OPEN) ? 'true' : 'false');
    }
    new MutationObserver(sync).observe(item, { attributes: true, attributeFilter: ['class'] });
    sync();

    // Escape returns focus to the trigger, which re-fires focusin on this same
    // item; without this guard the panel would reopen the instant it closed.
    var suppressOpen = false;

    function open() { if (!suppressOpen) item.classList.add(OPEN); }
    function close() { item.classList.remove(OPEN); }

    item.addEventListener('focusin', open);

    item.addEventListener('focusout', function () {
      // Let the browser settle on the new activeElement first.
      window.setTimeout(function () {
        if (item.contains(document.activeElement)) return;
        // Do not yank the panel away from a mouse user still hovering it.
        if (item.matches(':hover')) return;
        close();
      }, 0);
    });

    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (!item.classList.contains(OPEN)) return;
        e.preventDefault();
        suppressOpen = true;
        close();
        trigger.focus();
        window.setTimeout(function () { suppressOpen = false; }, 0);
        return;
      }

      // Arrow Down enters the panel. Enter is left to the link.
      if (e.key !== 'ArrowDown' && e.key !== 'Down') return;
      if (document.activeElement !== trigger) return;
      e.preventDefault();
      open();

      // hidden-focus-guard releases tabindex on a short debounce once the panel
      // becomes interactive, so retry until the target will accept focus.
      var tries = 20;
      (function attempt() {
        var targets = [].slice.call(sub.querySelectorAll(FOCUSABLE)).filter(isVisible);
        var first = targets[0];
        if (first) {
          first.focus();
          if (document.activeElement === first) return;
        }
        if (--tries > 0) window.setTimeout(attempt, 30);
      })();
    });
  }

  function init() {
    var items = document.querySelectorAll(ITEM);
    for (var i = 0; i < items.length; i++) wire(items[i]);
  }

  function start() {
    init();
    // Mega menu panels are lazy loaded, so pick up items added later.
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () { scheduled = false; init(); }, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
